import type React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import timerActions from 'actions/timer';
import Toggle from 'app/ui/components/atoms/toggle';
import type { ContentData } from 'app/ui/components/molecules/content-item-areas-wrapper';
import Timer from 'app/ui/components/molecules/timer';
import { useEffect } from 'react';
import useSectionManager from 'src/app/hooks/use-section-manager';
import { useCommunicationService } from 'src/app/services/communication';
import { MessageType } from 'src/app/utils/in-app-comunication/types';
import { useAppState, useDispatch } from 'state';
import TimerControls from '../timer/controls';

export interface SlideTimerWithControlsProps {
  finalDuration?: number;
  sectionData: ContentData;
  isPresenterView?: boolean;
}

const TimerContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.faqGrey};
  border-radius: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(6)};
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 4rem;
`;

const TitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleText = styled.h2`
  color: ${({ theme }) => theme.colors.white};
  font-size: 24px;
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
  font-weight: ${({ theme }) => theme.typography.bold};
`;

const TimerText = styled(TitleText)`
  font-weight: ${({ theme }) => theme.typography.light};
  margin: 0;
`;

const TimerContentContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const TimerArea = styled.div<{ timerEnabled: boolean; isWorkTime: boolean }>`
  background-color: ${({ theme, timerEnabled, isWorkTime }) => {
    if (timerEnabled) {
      if (isWorkTime) {
        return theme.colors.greenWithTransparency;
      }
      return theme.colors.redWithTransparency;
    }
    return theme.colors.veryGray;
  }};
    border: 2px solid ${({ theme, timerEnabled, isWorkTime }) => {
      if (timerEnabled) {
        if (isWorkTime) {
          return theme.colors.green;
        }
        return theme.colors.firebreathingRed;
      }
      return theme.colors.lightGray;
    }};
  border-radius: ${({ theme }) => theme.spacing(3)};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(6)};
`;

const WorkoutLabel = styled.div<{ timerEnabled: boolean }>`
  color: ${({ theme, timerEnabled }) => (timerEnabled ? theme.colors.white : theme.colors.lightGray)};
  font-size: 20px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  opacity: 0.9;
`;

const StyledTimer = styled(Timer)`
  color: ${({ theme }) => theme.colors.white};
  font-weight: bold;
  line-height: 1;

  ${({ theme: { media } }) => `${media.medium(
    () => `
      font-size: 5rem;
    `,
  )}
  `};
`;

const VerticalControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  width: 5rem;
`;

const TimerSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
`;

const TimerIndicator = styled(TimerText)<{ timerEnabled: boolean }>`
  color: ${({ theme, timerEnabled }) => (timerEnabled ? theme.colors.focoltone : theme.colors.white)};
  text-transform: uppercase;
`;

const SlideTimerWithControls: React.FC<SlideTimerWithControlsProps> = ({
  isPresenterView = true,
  finalDuration,
  sectionData,
}) => {
  const dispatch = useDispatch();
  const comms = useCommunicationService();
  const isTimerEnabled = useAppState((state) => state.timer.isTimerEnabled);
  const intl = useIntl();
  const hasWorkOrRestTime = !!sectionData.workTime || !!sectionData.restTime;
  const sectionConfig = hasWorkOrRestTime
    ? {
        workTime: sectionData.workTime || 0,
        restTime: sectionData.restTime || 0,
        rounds: sectionData.rounds || 1,
      }
    : undefined;

  const { isRunning, play, pause, restart, currentSectionState, reset } = useSectionManager({
    timerId: `section-${sectionData.id}-timer`,
    sectionConfig,
    finalDuration: finalDuration || sectionData.duration || 0,
  });
  const isWorkTime = currentSectionState.currentSection === 'Work';
  const timerText = intl.formatMessage({ id: 'whiteboard.timer' });
  const workoutText = isWorkTime
    ? intl.formatMessage({ id: 'whiteboard.workout' })
    : intl.formatMessage({ id: 'whiteboard.rest' });

  useEffect(() => {
    if (!isTimerEnabled && isRunning) {
      pause();
    }
  }, [isTimerEnabled, isRunning, pause]);

  // Send timer enabled state to other windows
  useEffect(() => {
    comms.sendMessage({
      type: MessageType.TIMER_ENABLED_CHANGE,
      isTimerEnabled,
      timestamp: Date.now(),
    });
  }, [isTimerEnabled, comms]);

  useEffect(() => {
    if (!isTimerEnabled) {
      pause();
    }
  }, [isTimerEnabled, pause]);

  return (
    <TimerContainer data-testid="trainer-timer-container">
      <TitleSection>
        <TitleText>{timerText}</TitleText>
        <TimerSection>
          <TimerText>Timer: </TimerText>
          <TimerIndicator timerEnabled={isTimerEnabled}>{isTimerEnabled ? 'On' : 'Off'}</TimerIndicator>
          <Toggle
            state={isTimerEnabled}
            onChange={() => dispatch(timerActions.toggleTimerEnabled())}
            data-testid="timer-toggle"
          />
        </TimerSection>
      </TitleSection>
      <TimerContentContainer>
        <TimerArea timerEnabled={isTimerEnabled} isWorkTime={isWorkTime} data-testid="trainer-timer-area">
          <WorkoutLabel timerEnabled={isTimerEnabled}>
            {hasWorkOrRestTime && `${currentSectionState.currentRound + 1} / ${sectionData.rounds} - `}
            {workoutText}
          </WorkoutLabel>
          <StyledTimer
            timerEnabled={isTimerEnabled}
            isPresenterView={isPresenterView}
            elapsedTime={currentSectionState.countdownTime}
            reset={reset}
          />
        </TimerArea>
        <VerticalControlsContainer>
          <TimerControls
            isRunning={isRunning}
            play={play}
            pause={pause}
            restart={restart}
            timerEnabled={isTimerEnabled}
          />
        </VerticalControlsContainer>
      </TimerContentContainer>
    </TimerContainer>
  );
};

export default SlideTimerWithControls;
